import React, { useState } from 'react';
import { Discount, DiscountResponse } from '../models/APIModels';
import { axiosKK } from '../services/networkRequest';
import Button from './Button';

interface DiscountCodeProps {
  successfulDiscount: (discount: Discount) => void;
}

/**
 * The discount field on the checkout page.
 *
 * Redesigned in Phase 4 Track 4.9. It was an unlabelled-in-practice input
 * beside a primary-filled "Submit" button, and its chrome lived inside the
 * `form.payment { … }` block that the Payment redesign deleted. Three things
 * were wrong with it beyond the styling:
 *
 *   - Pressing Enter in the field submitted the *payment* form, because the
 *     field sits inside it and the button was the only thing that applied a
 *     code. Typing a code and hitting Enter tried to take the payment.
 *   - A valid code produced no confirmation at all. The figures above
 *     changed, but nothing said the code had been accepted.
 *   - "Submit" in the brand's filled primary competed with "Confirm payment"
 *     for the one primary action on the page.
 */
function DiscountComponent(props: DiscountCodeProps): JSX.Element {
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [discountCode, setDiscountCode] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [applied, setApplied] = useState<Discount>();

  const apply = () => {
    if (submitting || discountCode.trim().length === 0) {
      return;
    }

    setSubmitting(true);
    setError('');

    axiosKK
      .get<DiscountResponse>(`/discountCode/${discountCode}`)
      .then(({ data }) => {
        const discount = data?.discount;
        if (discount?.deletedAt) {
          setError('Discount code not found or is no longer active.');
          return;
        }
        if (discount?.expires && new Date(discount.expires) < new Date()) {
          setError('This discount code has expired.');
          return;
        }
        setApplied(discount);
        props.successfulDiscount(discount);
      })
      .catch((err) => {
        console.error(err);
        const serverError: string = err?.response?.data?.error;
        setError(serverError || 'Something went wrong. Please try again.');
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const describedBy = error
    ? 'discount-error'
    : applied
    ? 'discount-applied'
    : undefined;

  return (
    <div className="discount">
      <label className="discount__label" htmlFor="discount">
        Discount code
      </label>

      <div className="discount__group">
        <input
          className="discount__input"
          id="discount"
          type="text"
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          placeholder="e.g. WELCOME10"
          value={discountCode}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          onChange={(event) => {
            setError('');
            setDiscountCode(event.target.value.toUpperCase());
          }}
          onKeyDown={(event) => {
            // The field lives inside the payment form, so Enter would
            // otherwise try to take the payment rather than apply the code.
            if (event.key === 'Enter') {
              event.preventDefault();
              apply();
            }
          }}
        />

        {/*
          Secondary, not primary: there is one primary action on a checkout
          page and it is the one that takes the money.
        */}
        <Button
          type="button"
          variant="secondary"
          size="md"
          className="discount__action"
          loading={submitting}
          disabled={submitting || discountCode.trim().length === 0}
          onClick={apply}
        >
          Apply
        </Button>
      </div>

      {error && (
        <p className="discount__message discount__message--error" id="discount-error" role="alert">
          {error}
        </p>
      )}

      {!error && applied && (
        <p
          className="discount__message discount__message--applied"
          id="discount-applied"
          role="status"
        >
          {applied.discountCode} applied — {applied.discountPercentage}% off
          {applied.recurring ? ' your monthly storage' : ''}
          {applied.upFront && applied.recurring ? ' and' : ''}
          {applied.upFront ? " what you pay today" : ''}.
        </p>
      )}
    </div>
  );
}

export default DiscountComponent;
